import { provideRtkDesignSystem } from '@cloudflare/realtimekit-ui';
import { applyButtonBrandRampToHost, hexToBrandRgbString } from './applyRtkBrandTokensForFocusRing';
import { collectElementsDeepShadow } from './applyScopedControlbarHoverBackground';
import { ensureRtkMeetingTextContrast } from './ensureRtkMeetingTextContrast';
import {
  hexToRgbSpaceSeparated,
  normalizeThemeHex,
  pickAccessibleForeground,
  relativeLuminance,
  resolveAccessibleForegroundForSurface,
} from './wcagContrast';

const BRAND_SHADES = ['300', '400', '500', '600', '700', '800'];
const TEXT_MIN = 4.5;

/** Injected into `rtk-menu-list` shadow: force slotted row `color` at rest + on hover (inherit/`provideRtkDesignSystem` alone often loses to chat tokens until `:hover`). */
const MENU_LIST_PRIMARY_ROW_STYLE_ID = 'wbs-primary-menu-list-row-contrast';

/** Secondary `rtk-menu-list` (e.g. incoming chat message menus): pale panel + **`rtk-menu-item.secondary`** — meeting tokens can wash row text; force slotted color like primary lists. */
const MENU_LIST_SECONDARY_ROW_STYLE_ID = 'wbs-secondary-menu-list-row-contrast';

/** @param {Element | null | undefined} el */
function menuListIsPrimaryVariant(el) {
  if (!el || el.localName !== 'rtk-menu-list') return false;
  return (
    el.classList.contains('primary') ||
    el.getAttribute('menu-variant') === 'primary'
  );
}

/**
 * WCAG-safe accent hex used for flattened `--rtk-colors-brand-*` on sidebar (same fill as primary `rtk-menu-list`).
 *
 * @param {object | null | undefined} themeDetails
 * @returns {{ surfaceHex: string, preferredHex: string, accentHex: string } | null}
 */
function resolveMeetingBrandAccent(themeDetails) {
  const preset = themeDetails?.presetTheme ?? {};
  const surfaceHex = normalizeThemeHex(
    themeDetails?.headerBackground ?? preset.headerBackground
  );
  const preferredHex =
    normalizeThemeHex(themeDetails?.buttonColor ?? preset.buttonColor) ??
    normalizeThemeHex(themeDetails?.headerColor ?? preset.headerColor);

  if (!surfaceHex || !preferredHex) return null;

  const accentHex =
    resolveAccessibleForegroundForSurface(surfaceHex, preferredHex, { minRatio: TEXT_MIN }) ??
    preferredHex;
  const normalized = normalizeThemeHex(accentHex);
  if (!normalized || !hexToBrandRgbString(normalized)) return null;

  return { surfaceHex, preferredHex, accentHex: normalized };
}

/**
 * `rtk-menu-list.primary` uses `brand-300` as its panel fill; **`rtk-menu-list`** without that variant uses a
 * pale chrome panel. In both cases meeting-scoped tokens can make **slotted** `rtk-menu-item` labels match the
 * fill at rest (**incoming** chat menus use **secondary** list + **secondary** items — easy to miss if we only
 * patch `.primary`). Inject **`::slotted(rtk-menu-item)`** with `!important` at rest + **`:hover`**; patch **every**
 * row host.
 *
 * **Primary and secondary** lists both use the same **chrome** text color (`onChromeHex` from
 * `headerBackground` + `headerColor`) for default and hover so labels stay one consistent color (e.g. black on
 * light themes) without a rest/hover flip; still WCAG vs the meeting header surface for dark themes.
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails
 */
export function applyPrimaryMenuListTextContrast(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot || typeof document === 'undefined') return;

  const preset = themeDetails?.presetTheme ?? {};

  const brand = resolveMeetingBrandAccent(themeDetails);

  // Derive surface even when brand accent is unavailable (incomplete theme data).
  // Fall back to headerBackground alone; pickAccessibleForeground will pick black/white.
  const surfaceHex =
    brand?.surfaceHex ??
    normalizeThemeHex(themeDetails?.headerBackground ?? preset.headerBackground) ??
    '#ffffff';

  const headerColorHint = normalizeThemeHex(themeDetails?.headerColor ?? preset.headerColor);
  const onChromeHex =
    pickAccessibleForeground(surfaceHex, headerColorHint ?? '#000000', { minRatio: TEXT_MIN }) ??
    '#000000';
  const onChromeRgb = hexToRgbSpaceSeparated(onChromeHex);

  // Primary `rtk-menu-list.primary` fill = `--rtk-colors-brand-300` from `applyButtonBrandRampToHost`
  // (`buttonBackground` on meeting/sidebar hosts). Derive label text against that surface; when chrome
  // text (`onChromeHex`) also passes on the panel, use it so labels match header chrome (Islamic Green).
  const primaryMenuBgHex =
    normalizeThemeHex(
      themeDetails?.buttonBackground ?? preset.buttonBackground ??
      themeDetails?.headerBackground ?? preset.headerBackground
    ) ?? surfaceHex;
  const panelTextHex =
    pickAccessibleForeground(primaryMenuBgHex, '#ffffff', { minRatio: TEXT_MIN }) ??
    pickAccessibleForeground(primaryMenuBgHex, '#000000', { minRatio: TEXT_MIN }) ??
    '#ffffff';
  const chromeOnPanel =
    onChromeHex &&
    pickAccessibleForeground(primaryMenuBgHex, onChromeHex, { minRatio: TEXT_MIN }) ===
      normalizeThemeHex(onChromeHex);
  const primaryMenuTextHex = chromeOnPanel ? onChromeHex : panelTextHex;
  const primaryMenuRgb = hexToRgbSpaceSeparated(primaryMenuTextHex) ?? onChromeRgb;

  const lists = collectElementsDeepShadow(meetingHostEl.shadowRoot, 'rtk-menu-list');

  for (const list of lists) {
    const sr = list.shadowRoot;
    if (!sr) continue;

    if (menuListIsPrimaryVariant(list)) {
      provideRtkDesignSystem(list, {
        colors: { text: primaryMenuTextHex },
      });

      let rowStyle = sr.getElementById(MENU_LIST_PRIMARY_ROW_STYLE_ID);
      if (!rowStyle) {
        rowStyle = document.createElement('style');
        rowStyle.id = MENU_LIST_PRIMARY_ROW_STYLE_ID;
        sr.appendChild(rowStyle);
      }
      rowStyle.textContent = `
      ::slotted(rtk-menu-item) {
        color: rgb(${primaryMenuRgb}) !important;
      }
      ::slotted(rtk-menu-item:hover),
      ::slotted(rtk-menu-item.primary:hover) {
        color: rgb(${primaryMenuRgb}) !important;
      }
    `;
    } else {
      provideRtkDesignSystem(list, {
        colors: { text: onChromeHex },
      });

      let secStyle = sr.getElementById(MENU_LIST_SECONDARY_ROW_STYLE_ID);
      if (!secStyle) {
        secStyle = document.createElement('style');
        secStyle.id = MENU_LIST_SECONDARY_ROW_STYLE_ID;
        sr.appendChild(secStyle);
      }
      secStyle.textContent = `
      ::slotted(rtk-menu-item) {
        color: rgb(${onChromeRgb}) !important;
      }
      ::slotted(rtk-menu-item:hover) {
        color: rgb(${onChromeRgb}) !important;
      }
    `;
    }
  }

  const menuItems = collectElementsDeepShadow(meetingHostEl.shadowRoot, 'rtk-menu-item');
  for (const item of menuItems) {
    const parentList = item.closest('rtk-menu-list');
    if (!parentList) continue;

    const textHex = menuListIsPrimaryVariant(parentList) ? primaryMenuTextHex : onChromeHex;
    provideRtkDesignSystem(item, {
      colors: { text: textHex },
    });
  }
}

/** Injected into `rtk-sidebar-ui` shadow: selected tab badge uses `text-700` on `brand-500`; dark accent + muted body text fails WCAG. */
const SIDEBAR_SELECTED_BADGE_STYLE_ID = 'wbs-selected-tab-badge-contrast';

/** Injected into `rtk-sidebar-ui` shadow: header title, close (ghost icon), and pinned slot must use `text-*` tokens — kit leaves `color` unset so inheritance can match the pink surface. */
const SIDEBAR_HEADER_CHROME_STYLE_ID = 'wbs-sidebar-header-chrome-contrast';

/**
 * `rtk-sidebar-ui` does not set `color` on `:host`; `h3`, ghost close `rtk-button`, and slotted pin
 * chrome inherit a computed color that can equal `background-900` on light themes. Pin tokens from
 * {@link ensureRtkMeetingTextContrast} onto host + header subtree + pinned slot.
 *
 * Call **after** {@link ensureRtkMeetingTextContrast} on the same element so `--rtk-colors-text-*` exists.
 *
 * @param {HTMLElement | null} sidebarUiEl
 */
export function ensureSidebarUiHeaderChromeContrast(sidebarUiEl) {
  if (!sidebarUiEl || sidebarUiEl.localName !== 'rtk-sidebar-ui' || typeof document === 'undefined') {
    return;
  }

  sidebarUiEl.style.setProperty('color', 'rgb(var(--rtk-colors-text-1000))');

  const sr = sidebarUiEl.shadowRoot;
  if (!sr) return;

  let style = sr.getElementById(SIDEBAR_HEADER_CHROME_STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = SIDEBAR_HEADER_CHROME_STYLE_ID;
    sr.appendChild(style);
  }
  style.textContent = `
    .main-header,
    .main-header h3,
    .close {
      color: rgb(var(--rtk-colors-text-1000)) !important;
    }
    .close rtk-icon svg {
      fill: currentColor !important;
    }
    slot[name="pinned-state"]::slotted(*) {
      color: rgb(var(--rtk-colors-text-1000)) !important;
    }
  `;
}

/**
 * `rtk-sidebar-ui` tabs/badges use `--rtk-colors-brand-*` on `headerBackground`. Meeting-level
 * brand is often `buttonBackground` (light), so pink-on-pink tabs (Islamic Pink). Drive tab accent from
 * WCAG-safe `buttonColor` vs sidebar surface; unchanged when the API pair already passes.
 *
 * @param {HTMLElement | null} sidebarUiEl
 * @param {object | null | undefined} themeDetails
 */
export function applySidebarUiBrandRampForContrast(sidebarUiEl, themeDetails) {
  if (!sidebarUiEl || sidebarUiEl.localName !== 'rtk-sidebar-ui' || typeof document === 'undefined') {
    return;
  }

  const brand = resolveMeetingBrandAccent(themeDetails);
  if (!brand) return;

  const { accentHex } = brand;
  const rgb = hexToBrandRgbString(accentHex);
  if (!rgb) return;

  for (const shade of BRAND_SHADES) {
    sidebarUiEl.style.setProperty(`--rtk-colors-brand-${shade}`, rgb);
  }

  const onAccentHex =
    pickAccessibleForeground(accentHex, '#ffffff', { minRatio: TEXT_MIN }) ?? '#ffffff';
  provideRtkDesignSystem(sidebarUiEl, {
    colors: { 'text-on-brand': onAccentHex },
  });

  ensureSidebarSelectedBadgeContrastStyle(sidebarUiEl, accentHex);
}

/**
 * Selected `.tab-participant-count-badge.selected-tab` keeps kit `color: text-700`; when accent fill is
 * dark, body `text-700` stays dark — force label to `text-on-brand-*` (set above).
 *
 * @param {HTMLElement | null} sidebarUiEl
 * @param {string} accentHex normalized `#rrggbb`
 */
export function ensureSidebarSelectedBadgeContrastStyle(sidebarUiEl, accentHex) {
  const sr = sidebarUiEl?.shadowRoot;
  if (!sr || typeof document === 'undefined') return;

  const normalized = normalizeThemeHex(accentHex);
  const L = normalized ? relativeLuminance(normalized) : null;
  let style = sr.getElementById(SIDEBAR_SELECTED_BADGE_STYLE_ID);
  if (L != null && L < 0.5) {
    if (!style) {
      style = document.createElement('style');
      style.id = SIDEBAR_SELECTED_BADGE_STYLE_ID;
      sr.appendChild(style);
    }
    style.textContent = `
      .tab-participant-count-badge.selected-tab {
        color: rgb(var(--rtk-colors-text-on-brand-1000)) !important;
      }
    `;
  } else if (style) {
    style.remove();
  }
}

/**
 * `rtk-avatar` fill uses `brand-500` (= `buttonBackground` on meeting). Initials use
 * `text-on-brand-*` / `text-*`.
 *
 * @param {HTMLElement | null} avatarEl
 * @param {object | null | undefined} themeDetails
 */
export function ensureAvatarInitialsContrast(avatarEl, themeDetails) {
  if (!avatarEl || typeof window === 'undefined') return;

  const preset = themeDetails?.presetTheme ?? {};
  const bgHex = normalizeThemeHex(
    themeDetails?.buttonBackground ?? preset.buttonBackground
  );
  if (!bgHex) return;

  const preferred =
    normalizeThemeHex(themeDetails?.buttonColor ?? preset.buttonColor) ?? '#ffffff';

  const resolved =
    pickAccessibleForeground(bgHex, preferred, { minRatio: TEXT_MIN }) ?? preferred;

  provideRtkDesignSystem(avatarEl, {
    colors: { 'text-on-brand': resolved },
  });
}

/**
 * Patch hosts that do not reliably inherit meeting-level tokens (nested shadows): stage tiles
 * (`rtk-avatar`, `rtk-name-tag`).
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails
 */
export function applyStageAndSidebarLeafPatches(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot) return;

  const preset = themeDetails?.presetTheme ?? {};
  const headerColor = themeDetails?.headerColor ?? preset.headerColor;
  const headerBackground = themeDetails?.headerBackground ?? preset.headerBackground;
  const buttonBg = themeDetails?.buttonBackground ?? preset.buttonBackground;

  const sr = meetingHostEl.shadowRoot;

  const avatars = collectElementsDeepShadow(sr, 'rtk-avatar');
  for (const el of avatars) {
    applyButtonBrandRampToHost(el, buttonBg);
    ensureAvatarInitialsContrast(el, themeDetails);
  }

  const nameTags = collectElementsDeepShadow(sr, 'rtk-name-tag');
  for (const el of nameTags) {
    ensureRtkMeetingTextContrast(el, {
      preferredTextHex: headerColor,
      headerBackground,
      surfaceBackgroundHex: headerBackground,
    });
  }

  /** Control-bar toggles (`rtk-chat-toggle` unread pill): same `brand-500` + `text-on-brand` math as avatars. */
  const toggleBadgeTags = ['rtk-chat-toggle', 'rtk-participants-toggle'];
  for (const tag of toggleBadgeTags) {
    for (const el of collectElementsDeepShadow(sr, tag)) {
      applyButtonBrandRampToHost(el, buttonBg);
      ensureAvatarInitialsContrast(el, themeDetails);
    }
  }
}

/**
 * Modals (`rtk-settings`, leave confirmation, etc.) render under `rtk-dialog-manager` → `rtk-dialog`.
 * Surfaces use `background-*` + `text-*`; primaries use `brand-*` + `text-on-brand-*`. Without tokens,
 * themed meetings keep dark defaults (illegible on light API surfaces).
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails
 */
export function applyDialogChromeContrast(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot) return;

  const preset = themeDetails?.presetTheme ?? {};
  const headerColor = themeDetails?.headerColor ?? preset.headerColor;
  const headerBackground =
    themeDetails?.headerBackground ?? preset.headerBackground;
  const buttonBg = themeDetails?.buttonBackground ?? preset.buttonBackground;

  const sr = meetingHostEl.shadowRoot;
  const DIALOG_HOSTS = ['rtk-dialog-manager', 'rtk-dialog'];

  for (const tag of DIALOG_HOSTS) {
    for (const el of collectElementsDeepShadow(sr, tag)) {
      ensureRtkMeetingTextContrast(el, {
        preferredTextHex: headerColor,
        headerBackground,
        surfaceBackgroundHex: headerBackground,
      });
      applyButtonBrandRampToHost(el, buttonBg);
      ensureAvatarInitialsContrast(el, themeDetails);
    }
  }
}
